import React from 'react';

import { AppConfig } from '../core-bundle/config/app-config';
import Icon from '@wetransfer/transfer/component/icon';
import { FF_OUTAGE_BANNER } from '@wetransfer/transfer/constant/experiments';
import { PLAN_TIER } from '@wetransfer/transfer/constant/plan-tier';
import { WORKSPACE_ROLES } from '@wetransfer/transfer/constant/workspaces';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';
import { getVariant } from '@wetransfer/transfer/modules/experiments';
import I18n from '@wetransfer/transfer/modules/i18n';
import { SUBSCRIPTION_STATUS } from '@wetransfer/transfer/store/types';

const ONEDAY = 24 * 60 * 60 * 1000;

export default {
  formatPriceCurrency(price, currency) {
    // currencies symbols that need to be placed in the end
    const currencies = ['kr'];

    if (currencies.includes(currency)) {
      return `${price} ${currency}`;
    }

    return `${currency} ${price}`;
  },

  sanitizeSubdomain(value) {
    return value
      .toLowerCase()
      .replace(/_|\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  },

  sanitizeHtml(s) {
    if (typeof s !== 'string') return s;

    return s
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  },

  parseSubscriptionDetails(subscription, planTier, order) {
    const { plan, ends_at, quantity: memberQuantity } = subscription;
    const { interval, currency } = plan;

    const currencySymbol = I18n.t('number.currency.' + currency.toUpperCase());

    const autorenewal_attempt = ends_at
      ? I18n.date(new Date(ends_at), 'without_day')
      : null;
    const period = I18n.t('datetime.prompts.' + interval);

    const expiry = new Date(ends_at);
    const daysBetween = this.getDaysBetweenDates(expiry, new Date());

    let planName = '';

    if (planTier) {
      planName = this.getPlanName(planTier);
    }

    const deal = order
      ? this.formatPriceCurrency(order.total_excluding_discount, currencySymbol)
      : '';

    return {
      deal,
      expiry: I18n.date(expiry, 'without_day'),
      autorenewal_attempt,
      daysBetween,
      period: period.toLowerCase(),
      number_of_members: memberQuantity,
      planName: planName,
      trialExpiryDate: I18n.date(new Date('2022-07-18'), 'without_day'),
    };
  },

  getTransferSizeInMB(size) {
    return (size / 1024 / 1024).toFixed(10);
  },

  getPlanName(planTier) {
    let planName = '';

    if (planTier === PLAN_TIER.PREMIUM_2022) {
      planName = 'WeTransfer Premium';
    } else if (planTier === PLAN_TIER.PRO_2022 || planTier === PLAN_TIER.PLUS) {
      planName = 'WeTransfer Pro';
    } else if (planTier === PLAN_TIER.FREE) {
      planName = 'WeTransfer Free';
    }

    return planName;
  },

  getWorkspaceName(currentTeam, user) {
    if (currentTeam) {
      if (currentTeam.defaultName) {
        const teamAdmin = currentTeam.members?.find(
          (member) => member.role === WORKSPACE_ROLES.ADMIN
        );

        return I18n.t('account.workspace.default_workspace', {
          name: teamAdmin.user.givenName,
        });
      }

      return currentTeam.name;
    }

    return I18n.t('account.workspace.default_workspace', {
      name: user.given_name,
    });
  },

  getWorkspaceInitials(user) {
    const firstName = user?.givenName || user?.given_name;
    const familyName = user?.familyName || user?.family_name;

    if (!user || !firstName) {
      return '';
    }

    return !familyName
      ? firstName.charAt(0)
      : firstName.charAt(0) + familyName.charAt(0);
  },

  getSubscriptionKey(subscription, planTier) {
    const {
      cancel_at_period_end,
      status,
      quantity: memberQuantity,
    } = subscription;

    const isSubscriptionOverdue =
      status === SUBSCRIPTION_STATUS.past_due ||
      status === SUBSCRIPTION_STATUS.unpaid;

    if (status === SUBSCRIPTION_STATUS.pending) {
      let planName = '';

      if (planTier === PLAN_TIER.PREMIUM_2022) {
        planName = 'WeTransfer Premium';
      } else if (
        planTier === PLAN_TIER.PRO_2022 ||
        planTier === PLAN_TIER.PLUS
      ) {
        planName = 'WeTransfer Pro';
      }

      return I18n.t('page.payment.never_actived_retry', { planName: planName });
    }

    if (status === SUBSCRIPTION_STATUS.pending)
      return 'page.payment.never_actived_notice'; //Your account is not active yet. Complete your registration to get started with Plus

    if (
      (status === SUBSCRIPTION_STATUS.active || isSubscriptionOverdue) &&
      !cancel_at_period_end
    )
      return memberQuantity > 1
        ? 'page.payment.autorenew_notice_teams'
        : 'page.payment.autorenew_notice'; //Your Plus subscription of %{deal} will be renewed automatically on %{autorenewal_attempt}.

    if (status === SUBSCRIPTION_STATUS.active && cancel_at_period_end) {
      //Oops - your Plus account has been cancelled and will be disabled in %{daysBetween} days.\nChanged your mind? Don’t panic, it’s easy to get things back up and running.

      return planTier === PLAN_TIER.PRO_2022 || planTier === PLAN_TIER.PLUS
        ? 'page.payment.subscription_reactivate_notice_pro'
        : 'page.payment.subscription_reactivate_notice_premium';
    }

    if (isSubscriptionOverdue || status === SUBSCRIPTION_STATUS.canceled)
      return 'page.payment.subscription_expired'; //Your Plus account has expired
  },

  generateSubscriptionText(subscription, planTier, order) {
    if (!subscription || !order) {
      return;
    }

    return I18n.t(
      this.getSubscriptionKey(subscription, planTier),
      this.parseSubscriptionDetails(subscription, planTier, order)
    );
  },

  getDateWithOffset(date, offset) {
    const newDate = new Date(date.valueOf());

    return new Date(newDate.setMonth(newDate.getMonth() + offset));
  },

  // Highlight text with React element so it's still safe and not XSS-able.
  highlightText(text, filterText, element = 'strong') {
    if (!text) {
      return null;
    }

    const lowercaseText = text.toLowerCase();

    // Only use filter if it matched this text (e.g. either email or label)
    let filter = filterText.toLowerCase();

    if (lowercaseText.indexOf(filter) === -1) {
      filter = '';
    }

    // Get the before, between, after highlightable sections
    const before = text.substring(0, lowercaseText.indexOf(filter));

    const between = text.substring(
      lowercaseText.indexOf(filter),
      lowercaseText.indexOf(filter) + filter.length
    );
    const after = text.substring(lowercaseText.indexOf(filter) + filter.length);

    const HighlightElementTag = element;

    return [
      before || null,
      <HighlightElementTag className="text--is-highlighted" key={text}>
        {between}
      </HighlightElementTag>,
      after || null,
    ];
  },

  highlightTextOnTwoLines(
    text,
    filterText,
    element = 'strong',
    key,
    showIcon = true
  ) {
    if (!text) {
      return null;
    }

    const lowercaseText = text.toLowerCase();

    // Only use filter if it matched this text (e.g. either email or label)
    let filter = filterText.toLowerCase();

    if (lowercaseText.indexOf(filter) === -1) {
      filter = '';
    }

    const firstPart = text.substring(
      lowercaseText.indexOf(filter) + filter.length,
      lowercaseText.indexOf(',')
    );
    const lastPart = text.substring(lowercaseText.indexOf(',') + 1);

    // Get the before, between, after highlightable sections
    const before = text.substring(0, lowercaseText.indexOf(filter));

    const between = text.substring(
      lowercaseText.indexOf(filter),
      lowercaseText.indexOf(filter) + filter.length
    );
    const HighlightElementTag = element;

    return [
      showIcon && (
        <Icon
          key="location_icon"
          type="Pin"
          className="autocomplete__item--icon"
        />
      ),
      <div className="text--highlighted--wrapper" key={key}>
        {before || null}
        <HighlightElementTag className="text--is-highlighted">
          {between}
        </HighlightElementTag>
        {firstPart}
        <HighlightElementTag className="text--last-part">
          {lastPart || null}
        </HighlightElementTag>
      </div>,
    ];
  },

  getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  },

  getAvatar(userId) {
    return AppConfig.avatars[userId % AppConfig.avatars.length];
  },

  getLastUsedSender() {
    let email = Cookie.get('wt_from') || '';
    email = email.split(',');

    return email.length ? email[0].replace(/\s*/g, '') : '';
  },

  getFileExtension(fileName) {
    if (fileName && fileName.indexOf('.') > -1) {
      return fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase();
    }

    return false;
  },

  getUserFrequency() {
    if (!Cookie.get(AppConfig.cookies.takeover.name)) {
      return 'new';
    }

    return 'exisiting';
  },

  // Probably temporary, during development some of the wt_sent cookie were set to `NaN`, resulting in undesired behaviour.
  getWTSent() {
    const cookie = Cookie.get('wt_sent');

    return parseInt(cookie === null || isNaN(cookie) ? 0 : cookie, 10);
  },

  getWTPlus() {
    const cookie = Cookie.get(AppConfig.cookies.plus.name);

    if (cookie === null || isNaN(cookie)) {
      return;
    }

    return parseInt(cookie, 10);
  },

  incrementWTSent() {
    Cookie.set('wt_sent', this.getWTSent() + 1, { days: 365 });
  },

  getWTDownloaded() {
    return parseInt(
      Cookie.get('wt_downloaded') === null || isNaN(Cookie.get('wt_downloaded'))
        ? 0
        : Cookie.get('wt_downloaded'),
      10
    );
  },

  incrementWTDownloaded() {
    Cookie.set('wt_downloaded', this.getWTDownloaded() + 1, { days: 365 });
  },

  validateEmail(email) {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const emojiRe =
      /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

    return re.test(email) && !emojiRe.test(email);
  },

  validateUri(uri) {
    const re = /^\S+\.\w{2,}.*$/;

    return re.test(uri);
  },

  scrollToElement(e, target, scrollElement) {
    if (!Browser.isMobile) {
      // Scroll to form
      e.preventDefault();
      e.stopPropagation();
      e.target.blur();
      scrollElement.scrollTo(target, { animated: true, duration: 200 });
    } else {
      target.scrollIntoView();
    }
  },

  getDaysSinceFirstVisit() {
    const firstVisitTimestamp = parseInt(
      Cookie.get(AppConfig.cookies.firstVisit.name)
    );

    if (firstVisitTimestamp) {
      return Math.floor((Date.now() - firstVisitTimestamp) / ONEDAY);
    }

    return null;
  },

  // TODO: I think it should be possible to do this without the
  // need for this function -- however, for compatibility with
  // existing cookies for now I'll just leave it in here.
  // http://stackoverflow.com/a/11252167
  treatAsUTC(date) {
    if (!date || typeof date['getTimezoneOffset'] !== 'function') {
      date = String(date).split('/');

      if (date.length !== 3) {
        return NaN;
      }
      date = new Date(date.join('/'));
    }

    const result = date;
    result.setMinutes(result.getMinutes() - result.getTimezoneOffset());

    return result;
  },

  getDaysBetweenDates(end, start) {
    return Math.floor(
      Math.abs(this.treatAsUTC(start) - this.treatAsUTC(end)) / ONEDAY
    );
  },

  getFirstVisitDate() {
    const firstVisitTimestamp = parseInt(
      Cookie.get(AppConfig.cookies.firstVisit.name),
      10
    );

    if (firstVisitTimestamp) {
      return new Date(firstVisitTimestamp).toDateString();
    }
  },

  getFormattedPrice(price, currency) {
    if (!currency) {
      return;
    }
    const upperCasedCurrency = currency.toUpperCase();
    // Get the symbol
    const currencyTranslation = I18n.t(`number.currency.${upperCasedCurrency}`);

    return this.formatPriceCurrency(price, currencyTranslation);
  },

  // Result will be the array of bank objects with updated keys suitable for form element
  // example input:
  // [{ name: 'ING', code: 'ing' }]
  // example output:
  // [{ label: 'ING', value: 'ing' }]
  parseCheckoutOptions(options) {
    const keysMap = {
      name: 'label',
      code: 'value',
    };

    const result = options.map((option) => {
      return Object.keys(option).reduce(
        (accumulator, key) => ({
          ...accumulator,
          ...{ [keysMap[key] || key]: option[key] },
        }),
        {}
      );
    });

    return result;
  },

  getOpenInAppUri({ shortUrl, utmContent = '' }) {
    const params = {
      link: shortUrl,
      ...AppConfig.mobileAppBanner.dynamicLinkParams,
      utm_campaign: utmContent,
    };

    const appUri = new URL(AppConfig.mobileAppBanner.dynamicLinkBaseUrl);

    for (const key in params) {
      appUri.searchParams.set(key, params[key]);
    }

    return new URL(appUri);
  },

  downloadAppUri() {
    if (Browser.isIphone) {
      return AppConfig.mobileAppBanner.downloadUrliOS;
    }

    if (Browser.isAndroid) {
      return AppConfig.mobileAppBanner.downloadUrlAndroid;
    }

    return AppConfig.mobileAppBanner.downloadUrl;
  },

  getFontsForStripe() {
    if (/https:/.test(location.protocol)) {
      return [
        {
          family: 'Fakt Pro Normal',
          src: `url('/assets/fonts/actiefgrotesque/ActiefGrotesque_W_Regular.woff')`,
        },
      ];
    }
  },

  getWallpaperPreviewType(previewType) {
    //On iPhones, the wallpaper preview does not work if it's a video tag
    //But it does work if it's a background image
    //Yes this is weird, yes it sucks, but hey, browsers.
    if (Browser.isIphone || Browser.isTablet) {
      return 'image';
    }

    return previewType;
  },

  getNoticeOptions({ incompatibleVersion, user }) {
    const outage = getVariant(FF_OUTAGE_BANNER);

    const { shouldShowFeatureBanner, featureTranslationKey, featureName } =
      AppConfig.featureBanner;

    // For non-positive messages, the notice color will be gray instead of the default blue
    if (outage === 'transfer_issues' || outage === 'general_issues') {
      return {
        text: I18n.t(`notice.${outage}`, {
          link: 'https://wetransfer.statuspage.io',
        }),
        color: 'dark',
      };
    } else if (incompatibleVersion) {
      return {
        text: I18n.t('force_update'),
        color: 'dark',
      };
    } else if (user.loggedIn && user.hasSkippedVerification) {
      return {
        text: I18n.t('notice.verification'),
        showCloseIcon: false,
      };
    } else if (
      shouldShowFeatureBanner &&
      Cookie.get(AppConfig.cookies.featureBanner.name) !== featureName
    ) {
      return {
        text: I18n.t(featureTranslationKey),
        onClose: () => {
          Cookie.set(AppConfig.cookies.featureBanner.name, featureName, {
            days: 31,
          });
        },
      };
    } else if (Browser.isOutdated(AppConfig.supportedBrowsers)) {
      return {
        text: I18n.t('transfer.outdated_browser.text'),
        color: 'dark',
      };
    }

    return { text: null, color: null };
  },

  isDefaultRecipientInUploadLocked({
    channelDefaultRecipient,
    userLoggedIn,
    channelName,
    accountSubDomainName,
  }) {
    const isDefaultRecipientSet = channelDefaultRecipient !== false;
    if (!isDefaultRecipientSet) return false;

    if (!userLoggedIn) return true;

    if (channelName === false) return false;

    return accountSubDomainName !== channelName;
  },

  isSenderLocked(user) {
    // User loggedIn state from Redux will be either
    // False: User not logged in
    // Integer: User's ID when logged in
    return user.loggedIn > 0 && user.isVerified && !user.hasSkippedVerification;
  },

  getManageTeamUrl() {
    const manageTeamUrl = new URL(`${AppConfig.accountAppUrl}/manage/team`);

    manageTeamUrl.searchParams.set('returnUrl', location.href);
    manageTeamUrl.searchParams.set('app', 'transfer');

    return manageTeamUrl.href;
  },

  isMobileIOS() {
    return (
      [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    );
  },

  getPortalsUrl() {
    const portalsUrl = new URL(`${AppConfig.portalsUrl}`);

    portalsUrl.searchParams.set('returnUrl', location.href);
    portalsUrl.searchParams.set('app', 'transfer');

    return portalsUrl.href;
  },

  getMonthPrice(price, tier, discountPercentage) {
    if (
      (tier === PLAN_TIER.PRO_2022 || tier === PLAN_TIER.PREMIUM_2022) &&
      price
    ) {
      const currency = price.replace(/[0-9]/g, '').trim();
      let monthPrice = parseInt(price.match(/\d+/)[0]) / 12;

      if (discountPercentage) {
        monthPrice = monthPrice - (monthPrice * discountPercentage) / 100;
      }

      const toFixed = monthPrice.toString().length > 3 ? 2 : 0;

      if (toFixed) {
        monthPrice = monthPrice.toFixed(toFixed);
      }

      price = this.formatPriceCurrency(monthPrice, currency);
    }

    return price;
  },

  getCurrentExperimentData(payload = []) {
    const dateNow = new Date().getTime();

    const discountData = payload.find((discount) => {
      const startDate = new Date(discount.startDate).getTime();
      const endDate = new Date(discount.endDate).getTime();

      const isNowAfterStartDate = dateNow >= startDate;
      const isNowBeforeEndDate = dateNow <= endDate;

      if (isNowAfterStartDate && isNowBeforeEndDate) {
        return discount;
      }
    });

    return discountData && discountData;
  },
};
